import SSWhiteText from "@core-components/title/components/SSWhiteText";
import { ELanguage, type Language } from "@helpers/I18nHelper";
import useAppLanguage from "@hooks/language/useAppLanguage";
import { Button, Group, Modal } from "@mantine/core";
import useLanguageStore from "@store/language.store";
import { useEffect, useState } from "react";

const LanguageSwitcherModal: React.FC = () => {
  const { lang, changeLanguage } = useAppLanguage();

  // Need to use translation map here
  // i18n can be a language and the text inside the modal can be different
  const translationMap = {
    fr: {
      title: "On dirait que vous naviguez en français",
      message:
        "Souhaitez-vous passer au site français pour plus de régions et d'offres ?",
      noThanks: "Non, merci",
      yesSwitch: "Oui, passer au français",
    },
    en: {
      title: "It looks like you're browsing in English",
      message:
        "Want to switch to the English site for more regions and offers?",
      noThanks: "No, Thanks",
      yesSwitch: "Yes, Switch to English",
    },
  };

  const [isOpened, setIsOpened] = useState(false);
  const { hasUserSelectedALanguage } = useLanguageStore();

  useEffect(() => {
    if (hasUserSelectedALanguage) {
      setIsOpened(false);
      return;
    }

    if (lang.browserLanguage !== lang.routeLanguage) {
      setIsOpened(true);
    } else {
      changeLanguage(lang.routeLanguage);
      setIsOpened(false);
    }
  }, [lang, changeLanguage, hasUserSelectedALanguage]);

  const handleClose = () => {
    setIsOpened(false);
  };

  const stayOnCurrentLanguage = () => {
    changeLanguage(lang.routeLanguage);
    handleClose();
  };

  const handleSwitchLanguage = (lang: Language) => {
    changeLanguage(lang);
    handleClose();
  };

  const getTranslationMap = () => {
    if (lang.browserLanguage === ELanguage.fr) {
      return translationMap.fr;
    }

    return translationMap.en;
  };

  return (
    <Modal
      centered
      classNames={{
        content: "bg-primary-default select-none rounded-lg",
        header: "bg-primary-default shadow-lg",
      }}
      closeButtonProps={{ className: "default-icon" }}
      opened={isOpened}
      title={
        <SSWhiteText className="font-black">
          {getTranslationMap().title}
        </SSWhiteText>
      }
      onClose={stayOnCurrentLanguage}
    >
      <div className="my-4">
        <SSWhiteText className="font-inter">
          {getTranslationMap().message}
        </SSWhiteText>
      </div>
      <Group className="w-full">
        <Button
          className="flex-1"
          classNames={{
            root: "bg-secret-blue-lighter disabled:grayscale text-primary-white hover:bg-secret-blue-lightest hover:text-primary-white border-none p-3 h-full",
          }}
          radius={8}
          variant="default"
          onClick={stayOnCurrentLanguage}
        >
          {getTranslationMap().noThanks}
        </Button>
        <Button
          className="flex-1"
          classNames={{
            root: "bg-secret-blue-default disabled:grayscale text-primary-white hover:bg-secret-blue-light hover:text-primary-white border-none p-3 h-full",
          }}
          radius={8}
          variant="default"
          onClick={() => handleSwitchLanguage(lang.browserLanguage)}
        >
          {getTranslationMap().yesSwitch}
        </Button>
      </Group>
    </Modal>
  );
};

export default LanguageSwitcherModal;
